/**
 * Undo/redo zásobník (Epic 13). `canvas/store.tsx` (Epic 6) a `schema/store.tsx`
 * (Epic 4) jsou dva nezávislé store/kontexty s vlastním `execute()` — několik
 * handlerů ale volá OBA v jednom uživatelském gestu a musí se vrátit jako
 * JEDEN krok (`DeleteTableDialog.tsx`, `DeleteColumnDialog.tsx`). Proto je
 * zásobník jeden, sdílený, NAD oběma store (viz `command/types.ts` hlavička),
 * ne dva nezávislé.
 *
 * `CommandStack` je záměrně obyčejná třída bez závislosti na Reactu — pushuje/
 * poppuje pole, nic víc — aby šla otestovat čistými unit testy stejně jako
 * zbytek command vrstvy (`canvas/commands.test.ts`), bez nutnosti zavádět do
 * projektu React testing infrastrukturu, která dnes nikde jinde není použitá.
 * `CommandStackProvider` je jen tenký React obal kolem jedné stabilní
 * instance.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { MessageKey } from '../i18n';
import type { Command } from './types';

export type StoreKind = 'canvas' | 'schema';
type Direction = 'forward' | 'backward';
// `Command<any, any>` je záměrné — zásobník ukládá commandy ze dvou různých
// store (`CanvasMetadata`/`SchemaModel`, každý s vlastním `CommandType`
// union) opaně a sám `apply`/`revert` nikdy nevolá, jen je předává applieru
// registrovanému příslušným store. Jediný `any` (ne oba) by nestačilo:
// `Command<any>` by nechal `TType` defaultovat na obecné `string`, a proti
// konkrétnímu `type: CanvasCommandType` by pak `string` neprošel — potřeba
// `any` na OBOU pozicích.
type Applier = (command: Command<any, any>, direction: Direction) => void;

interface StackItem {
  store: StoreKind;
  command: Command<any, any>;
}

export interface StackEntry {
  items: StackItem[];
  descriptionKey: MessageKey;
  descriptionParams?: Record<string, string | number>;
}

export interface GroupDescription {
  key: MessageKey;
  params?: Record<string, string | number>;
}

/** Doc §9 — "řádově stovky", nejstarší položky se při přetečení odsouvají. */
const MAX_STACK_DEPTH = 200;

/**
 * Čistá (bez Reactu) logika zásobníku. `record()` volá `execute()` KAŽDÉHO
 * store po úspěšné aplikaci commandu — mimo `runGroup()` se rovnou stane
 * jedním novým `StackEntry`, uvnitř `runGroup()` se nachytá do rozpracované
 * skupiny. `undo()`/`redo()` procházejí `items` napříč OBĚMA store v přesném
 * (resp. přesně opačném) pořadí, ve kterém byly provedeny — díky tomu
 * `DeleteTableDialog`-style handler (schema delete + N canvas delete-note +
 * clear-canvas-state) vrátí jedním Ctrl+Z i objekt, i jeho poznámky, ve
 * správném pořadí bez ohledu na to, že šlo o dva různé store.
 */
export class CommandStack {
  private undoStack: StackEntry[] = [];
  private redoStack: StackEntry[] = [];
  private appliers: Partial<Record<StoreKind, Applier>> = {};
  private pendingGroup: StackEntry | null = null;
  private readonly onChange: () => void;

  constructor(onChange: () => void = () => {}) {
    this.onChange = onChange;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  get undoEntry(): StackEntry | null {
    return this.undoStack[this.undoStack.length - 1] ?? null;
  }

  get redoEntry(): StackEntry | null {
    return this.redoStack[this.redoStack.length - 1] ?? null;
  }

  setApplier = (store: StoreKind, applier: Applier | null): void => {
    if (applier) this.appliers[store] = applier;
    else delete this.appliers[store];
  };

  private pushEntry(entry: StackEntry): void {
    this.undoStack.push(entry);
    if (this.undoStack.length > MAX_STACK_DEPTH) this.undoStack.shift();
    // Nová operace vždy zahodí redo větev (standardní lineární undo, E13-US2 AC).
    this.redoStack = [];
    this.onChange();
  }

  record = (store: StoreKind, command: Command<any, any>): void => {
    if (this.pendingGroup) {
      this.pendingGroup.items.push({ store, command });
      return;
    }
    this.pushEntry({
      items: [{ store, command }],
      descriptionKey: command.descriptionKey,
      descriptionParams: command.descriptionParams,
    });
  };

  /**
   * Seskupí všechny `record()` volání provedené synchronně uvnitř `fn` do
   * JEDNOHO `StackEntry` s explicitním popisem (ne odvozeným z dílčích
   * commandů — u smazání tabulky např. "smazání tabulky", ne "smazání
   * tabulky + smazání poznámky + ..."). Vnořené volání (nemělo by dnes nastat)
   * se přidá do stejné rozpracované skupiny, aby se seskupení nerozbilo.
   */
  runGroup = <T,>(description: GroupDescription, fn: () => T): T => {
    const isOutermost = !this.pendingGroup;
    if (isOutermost) {
      this.pendingGroup = { items: [], descriptionKey: description.key, descriptionParams: description.params };
    }
    try {
      return fn();
    } finally {
      if (isOutermost) {
        const entry = this.pendingGroup;
        this.pendingGroup = null;
        if (entry && entry.items.length > 0) this.pushEntry(entry);
      }
    }
  };

  undo = (): void => {
    const entry = this.undoStack.pop();
    if (!entry) return;
    for (let i = entry.items.length - 1; i >= 0; i -= 1) {
      const item = entry.items[i];
      this.appliers[item.store]?.(item.command, 'backward');
    }
    this.redoStack.push(entry);
    this.onChange();
  };

  redo = (): void => {
    const entry = this.redoStack.pop();
    if (!entry) return;
    for (const item of entry.items) {
      this.appliers[item.store]?.(item.command, 'forward');
    }
    this.undoStack.push(entry);
    this.onChange();
  };

  /** Připraveno i pro budoucí Epic 2 hook — po dokončení DB syncu/mergu se zásobník musí
   * vyprázdnit, protože předchozí commandy by se vztahovaly k modelu před sloučením
   * (E13-US5 AC). Dokud Epic 2 neexistuje, volá se jen implicitně remountem `ProjectPage`. */
  clear = (): void => {
    if (this.undoStack.length === 0 && this.redoStack.length === 0) return;
    this.undoStack = [];
    this.redoStack = [];
    this.onChange();
  };
}

interface CommandStackContextValue {
  stack: CommandStack;
  /** Mění identitu při každé mutaci zásobníku — jediný způsob, jak donutit
   * `useContext` konzumenty (tlačítka Zpět/Vpřed) k re-renderu, protože `stack`
   * samotná instance je napříč renderama stabilní (viz komentář u `CommandStackProvider`). */
  version: number;
}

const CommandStackContext = createContext<CommandStackContextValue | null>(null);

function isTextInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable;
}

export function CommandStackProvider({ children }: { children: ReactNode }) {
  const [version, setVersion] = useState(0);
  // Líné vytvoření JEDNÉ stabilní instance (`useRef`, ne `useMemo` — ten React
  // nezaručuje zachovat napříč renderama). `onChange` bumpne `version`, což
  // přes context hodnotu (viz níže) vynutí re-render konzumentů — `stack` sama
  // zůstává tentýž objekt, `useContext` by na mutaci uvnitř něj sám o sobě
  // nezareagoval.
  const stackRef = useRef<CommandStack | null>(null);
  if (!stackRef.current) {
    stackRef.current = new CommandStack(() => setVersion((value) => value + 1));
  }
  const stack = stackRef.current;

  // Aktivní po celou dobu otevřeného projektu (ne vázané na fokus canvasu) —
  // ignoruje textová pole, aby nekolidovalo s Ctrl+Z v inspektorových inputech.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z') return;
      if (isTextInput(event.target)) return;
      event.preventDefault();
      if (event.shiftKey) stack.redo();
      else stack.undo();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [stack]);

  const value = useMemo<CommandStackContextValue>(() => ({ stack, version }), [stack, version]);

  return <CommandStackContext.Provider value={value}>{children}</CommandStackContext.Provider>;
}

function useCommandStackContext(): CommandStackContextValue {
  const context = useContext(CommandStackContext);
  if (!context) throw new Error('useCommandStack musí být uvnitř CommandStackProvider.');
  return context;
}

/** Reaktivní veřejné API pro UI (tlačítka/zkratky Zpět/Vpřed, E13-US2/US3). */
export function useCommandStack() {
  const { stack } = useCommandStackContext();
  return {
    canUndo: stack.canUndo,
    canRedo: stack.canRedo,
    undoEntry: stack.undoEntry,
    redoEntry: stack.redoEntry,
    undo: stack.undo,
    redo: stack.redo,
    runGroup: stack.runGroup,
    clear: stack.clear,
  };
}

/** Nereaktivní surová instance — jen pro `canvas/store.tsx`/`schema/store.tsx`
 * (`record`/`setApplier`), které stav zásobníku samy nezobrazují. */
export function useCommandStackInstance(): CommandStack {
  return useCommandStackContext().stack;
}
